import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, TrendingUp, Shield, Users, Award, Building2, FileCheck, Calculator, PieChart, BookOpen, Briefcase, BarChart3, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const services = [
    {
      id: 'accounting-bookkeeping',
      icon: BookOpen,
      title: 'Accounting & Bookkeeping',
      description: 'Professional accounting services maintaining accurate financial records for your business.',
    },
    {
      id: 'auditing-assurance',
      icon: BarChart3,
      title: 'Auditing & Assurance',
      description: 'Thorough audit services providing credibility and confidence to stakeholders.',
    },
    {
      id: 'tax-advisory',
      icon: Calculator,
      title: 'Tax Advisory',
      description: 'Strategic tax planning and advisory services to optimize your tax liabilities.',
    },
    {
      id: 'gst-services',
      icon: PieChart,
      title: 'GST Services',
      description: 'Comprehensive GST registration, filing, and advisory for smooth business operations.',
    },
    {
      id: 'company-registration',
      icon: Building2,
      title: 'Company Registration',
      description: 'Seamless company and LLP registration services with expert guidance throughout the process.',
    },
    {
      id: 'compliance-services',
      icon: FileCheck,
      title: 'Compliance Services',
      description: 'Complete compliance solutions for companies and LLPs to ensure regulatory adherence.',
    },
  ];

  const whyChooseUs = [
    {
      id: 'trusted-expertise',
      icon: Shield,
      title: 'Trusted Expertise',
      description: 'Years of experience serving businesses across Mumbai with proven results.',
    },
    {
      id: 'client-centric',
      icon: Users,
      title: 'Client-Centric Approach',
      description: 'Personalized solutions tailored to your unique business requirements.',
    },
    {
      id: 'growth-partnership',
      icon: TrendingUp,
      title: 'Growth Partnership',
      description: 'We grow with you, providing scalable financial solutions at every stage.',
    },
    {
      id: 'professional-excellence',
      icon: Award,
      title: 'Professional Excellence',
      description: 'Committed to delivering the highest standards of professional service.',
    },
  ];

  const testimonials = [
    {
      id: 'vishal-kapadia',
      name: 'Vishal Kapadia',
      company: 'Owner, Tejj Textiles',
      content: 'Working with Kanish has been really smooth and he takes care of GST, TDS, and all compliances without any stress. He also explains the legal side clearly and guides you through everything. Super reliable and always on time.',
      rating: 5,
    },
    {
      id: 'dharmil-shah',
      name: 'Dharmil Shah',
      company: 'Client Servicing Director, DDB Mudra',
      content: 'Great experience overall. The team is very approachable and takes the time to understand your requirements before suggesting solutions. They helped me with tax filing and compliance, and everything was done seamlessly without any last-minute stress. Their attention to detail and proactive follow-ups really stand out. Would definitely recommend their services.',
      rating: 5,
    },
    {
      id: 'keya-jagirdar',
      name: 'Keya Jagirdar',
      company: 'Interior Designer',
      content: 'If "sorted" were a firm, it would be Kanish Jariwala & Associates. From staying on top of deadlines to making sense of even the tiniest details, Kanish and his team make everything feel effortless. Super reliable, highly skilled, and honestly just great to work with.',
      rating: 5,
    },
    {
      id: 'ayushi-chheda',
      name: 'Ayushi Chheda',
      company: 'Account Manager at Kickstart Media',
      content: 'Navigating finances on your own can be overwhelming, especially if it\'s your first time. I couldn\'t recommend anyone better than Kanish. He not only guides you through the entire process but does so with professionalism, patience, and genuine care.',
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1497366754035-f200968a6e72"
            alt="Professional Office"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 to-transparent"></div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>

        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            {/* Main Heading */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight">
              Professional{' '}
              <span className="text-blue-700">
                Chartered Accountant
              </span>{' '}
              Services in Mumbai
            </h1>

            {/* Subheading */}
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Expert financial solutions for your business growth. From company registration to tax advisory, 
              we provide comprehensive CA services tailored to your needs.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
              <Link to="/booking">
                <Button className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
                  Book Free Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" className="border-2 border-teal-600 text-teal-700 hover:bg-teal-50 px-8 py-6 text-lg rounded-xl transform hover:scale-105 transition-all duration-300">
                  Explore Services
                </Button>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-16 max-w-3xl mx-auto">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-700">100+</div>
                <div className="text-gray-600 mt-2">Happy Clients</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-700">5+</div>
                <div className="text-gray-600 mt-2">Years of Excellence</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-700">100%</div>
                <div className="text-gray-600 mt-2">End to End Support</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-700">500+</div>
                <div className="text-gray-600 mt-2">Cases Handled</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Professional Services
            </h2>
            <p className="text-xl text-gray-600">
              Comprehensive financial and compliance solutions designed to help your business thrive
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card 
                key={service.id}
                className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-blue-200 cursor-pointer transform hover:-translate-y-2"
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-700 transition-colors duration-300">
                    <service.icon className="text-blue-700 group-hover:text-white transition-colors duration-300" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-blue-700 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <Link to="/services" className="inline-flex items-center text-blue-700 font-medium group-hover:gap-3 transition-all">
                    Learn More
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-6 text-lg rounded-xl">
                View All Services
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Choose Us
            </h2>
            <p className="text-xl text-gray-600">
              Your success is our priority. Here's what sets us apart
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item) => (
              <div 
                key={item.id}
                className="text-center space-y-4 p-8 rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                  <item.icon className="text-blue-700" size={36} />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Client Testimonials
            </h2>
            <p className="text-xl text-gray-600">
              Don't just take our word for it - hear from our satisfied clients
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="hover:shadow-2xl transition-all duration-300 border-2">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`${testimonial.id}-star-${i}`} className="text-yellow-400 fill-yellow-400" size={20} />
                    ))}
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-6 italic">
                    "{testimonial.content}"
                  </p>
                  <div className="border-t pt-4">
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.company}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
